import { motion, useReducedMotion } from 'framer-motion'
import { LayoutDashboard, PencilLine, Share2 } from 'lucide-react'
import { useLanguage } from '../../contexts/LanguageContext'

// Pastel icon-chip gradients from the mockup (.step .ic), one per step.
const CHIP_BG = [
  'linear-gradient(135deg, #ffe0ec, #ffd0c2)',
  'linear-gradient(135deg, #e9defb, #d7ecff)',
  'linear-gradient(135deg, #fdf0c8, #d6f7e8)',
]
const CHIP_TEXT = ['text-celebration-pink', 'text-celebration-grape', 'text-celebration-coral']

// Three steps as white bubble cards (mockup .step): pastel icon chip, giant
// ghost number, springy hover lift. Cards pop in with a stagger on scroll.
const HowItWorks = () => {
  const { language } = useLanguage()
  const isKa = language === 'ka'
  const reduce = useReducedMotion()

  const steps = [
    {
      Icon: PencilLine,
      title: isKa ? 'შექმენით თქვენი მოსაწვევი' : 'Create your invitation',
      body: isKa
        ? 'აირჩიეთ დიზაინი და შეავსეთ დეტალები სულ რამდენიმე წუთში.'
        : 'Pick a design and fill in the details — in just a few minutes.',
    },
    {
      Icon: Share2,
      title: isKa ? 'გაუზიარეთ სტუმრებს' : 'Share with your guests',
      body: isKa
        ? 'გააგზავნეთ პერსონალიზებული მოსაწვევი ნებისმიერი საკომუნიკაციო არხით.'
        : 'Send a personalized invitation through any channel you like.',
    },
    {
      Icon: LayoutDashboard,
      title: isKa ? 'მართეთ ყველაფერი ერთად' : 'Manage everything together',
      body: isKa
        ? 'თვალი ადევნეთ სტუმრების პასუხებს, საჩუქრებსა და სიას ერთ სივრცეში.'
        : 'Track guest responses, gifts and the guest list in one space.',
    },
  ]

  return (
    <section className="relative">
      {/* Candy blob behind the cards */}
      <div
        aria-hidden="true"
        className="blob blob-sky blob-drift"
        style={{ width: 380, height: 380, top: 40, right: -180, opacity: 0.28 }}
      />
      <div className="relative mx-auto max-w-6xl px-5 py-16 lg:py-24">
        <h2 className="mx-auto max-w-[44ch] text-center font-lynx text-[clamp(1.9rem,3.5vw,2.75rem)] font-semibold tracking-tight">
          {isKa ? 'როგორ მუშაობს' : 'How it works'}
        </h2>

        <ol className="mt-14 grid gap-6 sm:grid-cols-3 sm:gap-7">
          {steps.map(({ Icon, title, body }, i) => (
            <motion.li
              key={title}
              initial={reduce ? false : { opacity: 0, y: 46, scale: 0.96 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ type: 'spring', stiffness: 220, damping: 18, delay: i * 0.12 }}
              className="group tile relative overflow-hidden rounded-bubble p-8"
            >
              {/* Giant ghost step number (mockup .step .num) */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute right-6 top-4 font-display text-[3.4rem] font-extrabold leading-none text-celebration-ink/5 dark:text-white/10"
              >
                {String(i + 1).padStart(2, '0')}
              </span>
              <span
                className={`relative flex h-16 w-16 items-center justify-center rounded-[20px] transition-transform duration-300 ease-spring group-hover:-rotate-6 group-hover:scale-110 ${CHIP_TEXT[i]}`}
                style={{ background: CHIP_BG[i] }}
              >
                <Icon className="h-7 w-7" />
              </span>
              <h3 className="relative mt-5 font-display text-xl font-bold tracking-tight">{title}</h3>
              <p className="relative mt-2 max-w-[34ch] text-[15px] leading-relaxed text-celebration-ink-2 dark:text-[#c3b8dd]">
                {body}
              </p>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  )
}

export default HowItWorks
